import { taggedTemplateLiteral as _taggedTemplateLiteral, objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import styled from '../../theming/styled-components.js';
import themes from '../../theming/themes.js';
import React, { memo, useRef, useState, useEffect } from 'react';
import withDefaultTheme from '../../hocs/withDefaultTheme.js';
import _Icon from '../Icon/index.js';
import { Tooltip } from '@mantine/core';

var _excluded = ["theme", "hasIcon", "underline", "iconColor", "iconName", "iconSize", "label", "children", "onOpenHover", "onOpenClick", "onTooltipClose"];
var _templateObject;
// maintains minimum 24/24 px size for WCAG/ADA rules
var StyledTooltipButton = styled('button')(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  background: none;\n  border: none;\n  width: fit-content;\n  min-idth: 24px;\n  min-height: 24px;\n  text-align: left;\n  color: inherit;\n  ", "\n  ", "\n"])), function (_ref) {
  var theme = _ref.theme,
    underline = _ref.underline;
  return underline === 'always' && "\n    text-decoration: 2px underline dotted ".concat(theme.palette.secondary, ";\n  ");
}, function (_ref2) {
  var theme = _ref2.theme,
    underline = _ref2.underline,
    isOpen = _ref2.isOpen;
  return underline === 'hover' && isOpen && "\n    &:hover, &:focus {\n      text-decoration: 2px underline dotted ".concat(theme.palette.secondary, "\n    }\n  ");
});
var ToolTip = function ToolTip(_ref3) {
  var _ref3$theme = _ref3.theme,
    theme = _ref3$theme === void 0 ? themes["default"] : _ref3$theme,
    _ref3$hasIcon = _ref3.hasIcon,
    hasIcon = _ref3$hasIcon === void 0 ? true : _ref3$hasIcon,
    _ref3$underline = _ref3.underline,
    underline = _ref3$underline === void 0 ? 'hover' : _ref3$underline,
    _ref3$iconColor = _ref3.iconColor,
    iconColor = _ref3$iconColor === void 0 ? 'secondary' : _ref3$iconColor,
    _ref3$iconName = _ref3.iconName,
    iconName = _ref3$iconName === void 0 ? 'info-light' : _ref3$iconName,
    _ref3$iconSize = _ref3.iconSize,
    iconSize = _ref3$iconSize === void 0 ? 'xs' : _ref3$iconSize,
    label = _ref3.label,
    children = _ref3.children,
    onOpenHover = _ref3.onOpenHover,
    onOpenClick = _ref3.onOpenClick,
    onTooltipClose = _ref3.onTooltipClose,
    rest = _objectWithoutProperties(_ref3, _excluded);
  if (!children && !hasIcon) {
    throw Error('Cannot use no Icon option without passing children');
  }
  // manual control over tooltip open/close for custom events
  var buttonRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    opened = _useState2[0],
    setOpened = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    clickedOpen = _useState4[0],
    setClickedOpen = _useState4[1]; // 👈 tracks click-to-toggle mode
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    focusedOpen = _useState6[0],
    setFocusedOpen = _useState6[1]; // 👈 helps to prevent mixed inputs from closing on mouse enter/leave
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isHovering = _useState8[0],
    setisHovering = _useState8[1];
  // ❌ closes on esc
  useEffect(function () {
    var handleKeyDown = function handleKeyDown(e) {
      if (e.key === 'Escape') {
        setClickedOpen(false);
        setOpened(false);
        if (onTooltipClose) onTooltipClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return function () {
      return window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  // 🖱️ click & toggle
  var handleClick = function handleClick() {
    var _buttonRef$current;
    if (!clickedOpen) {
      setClickedOpen(true);
      setOpened(true);
    } else {
      setClickedOpen(false);
      setOpened(false);
    }
    (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 || _buttonRef$current.focus();
  };
  // 🐭 enter & leave hover
  var handleMouseEnter = function handleMouseEnter() {
    if (!clickedOpen) {
      setisHovering(true);
      setOpened(true);
      if (opened) return;
      if (onOpenHover) onOpenHover();
    }
  };
  var handleMouseLeave = function handleMouseLeave() {
    if (!clickedOpen && !focusedOpen) {
      setisHovering(false);
      setOpened(false);
      if (onTooltipClose) onTooltipClose();
    }
  };
  var handleBlur = function handleBlur() {
    setClickedOpen(false);
    setisHovering(false);
    setFocusedOpen(false);
    setOpened(false);
    if (onTooltipClose) onTooltipClose();
  };
  var handleFocus = function handleFocus() {
    setOpened(true);
    if (onOpenClick && !isHovering) onOpenClick();
    setFocusedOpen(true);
  };
  return /*#__PURE__*/React.createElement(Tooltip, _extends({
    label: label,
    opened: opened,
    multiline: true,
    withArrow: true,
    arrowSize: 10,
    color: theme.palette.grayLightest,
    styles: function styles() {
      return {
        tooltip: {
          color: 'black',
          fontSize: '.875rem',
          fontWeight: 400,
          border: "1px solid ".concat(theme.palette.secondary),
          backgroundColor: 'white',
          boxShadow: '2px 2px 2px rgba(0,0,0, 0.2)',
          maxWidth: '450px'
        },
        arrow: {
          border: "1px solid ".concat(theme.palette.secondary),
          background: 'white'
        }
      };
    },
    transitionProps: {
      transition: 'pop',
      //or fade if this is too much
      duration: 150,
      timingFunction: 'ease'
    }
  }, rest), /*#__PURE__*/React.createElement(StyledTooltipButton, {
    ref: buttonRef,
    onClick: handleClick,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    underline: underline,
    isOpen: opened,
    theme: theme
  }, children, hasIcon && /*#__PURE__*/React.createElement(_Icon, {
    style: {
      marginLeft: children ? '.25rem' : undefined
    },
    name: iconName,
    color: iconColor,
    size: iconSize
  })));
};
var _Tooltip = withDefaultTheme(/*#__PURE__*/memo(ToolTip));

export default _Tooltip;
